import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import cloneDeep from 'lodash/cloneDeep'

import {
    ActivityRegion,
    ICalendar,
    LocationCalendars,
    ModalsVisibleState
} from 'pages/my-calendars/add-calendar/interfaces'
import { CalendarActions } from './components/calendar-actions'
import { calendarsSelector, userInfoSelector } from 'redux/selectors'
import { Calendar } from 'pages/my-calendars/components'
import { Layout } from 'shared/components/layout/Layout'
import {
    approveCalendar,
    createCalendar,
    deleteCalendar,
    getCalendarTypes,
    saveCalendar
} from 'redux/actions'
import { ADD_CALENDAR, ADD_CALENDAR_FORM, MY_CALENDARS } from 'shared/consts'
import './addCalendar.scss'
import {
    CalendarActionType,
    CalendarType,
    LocationType,
    OpenBtnType
} from 'pages/my-calendars/enums'
import { filterItemsWithDates, getDefaultLocationCalendar } from 'shared/utils'
import { useAppDispatch } from 'redux/hooks'
import {
    ApproveConfirmModal,
    BigGapModal,
    DeleteConfirmModal,
    EmptyFieldsModal,
    ModifyErrorModal,
    SuccessSaveModal
} from './components/modals'
import { getEmptyFieldsModalText, validateCalendar } from './utils'

const { Approve, Save } = CalendarActionType

export const AddCalendar: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const appDispatch = useAppDispatch()
    const {
        calendarId,
        isApproveSuccess,
        isApprovedCalendarModifyError,
        isAutoSave,
        isSaveSuccess,
        types,
        wasSaved
    } = useSelector(calendarsSelector)
    const [regionData, setRegionData] = useState<ActivityRegion[]>([])
    const [calendarData, setCalendarData] = useState<ICalendar | null>(null)
    const [modalsVisibleState, setModalsVisibleState] = useState<ModalsVisibleState>({
        emptyFields: false,
        bigGap: false,
        successSave: false,
        approveConfirm: false,
        modifyError: false,
        deleteConfirm: false
    })
    const [openBtnState, setOpenBtnState] = useState<OpenBtnType>(OpenBtnType.Shops)
    const [calendarActionState, setCalendarActionState] = useState<CalendarActionType | null>(null)
    const [isNeedToErase, setIsNeedToErase] = useState(false)
    const [locationCalendars, setLocationCalendars] = useState<LocationCalendars[]>([
        getDefaultLocationCalendar()
    ])

    const [emptyFieldsProps, setEmptyFieldsProps] = useState({
        text: '',
        isError: false
    })

    // отключаем возможность редактирования утвержденных календарей
    // const isSuperAdmin = checkSuperAdminRole()
    const isSuperAdmin = false

    useEffect(() => {
        dispatch(createCalendar.cancel())
        if (!calendarId && history.location.pathname.includes(ADD_CALENDAR)) {
            history.push(ADD_CALENDAR_FORM)
        }

        !types.length && dispatch(getCalendarTypes.request())
    }, [])

    useEffect(() => {
        isNeedToErase && setIsNeedToErase(false)
    }, [isNeedToErase])

    useEffect(() => {
        if (isSaveSuccess && !isAutoSave) {
            setModalsVisibleState({
                ...modalsVisibleState,
                successSave: true
            })
            dispatch(saveCalendar.cancel())
        }
    }, [isSaveSuccess, isAutoSave])

    useEffect(() => {
        if (isApproveSuccess) {
            setModalsVisibleState({
                ...modalsVisibleState,
                successSave: true
            })
            dispatch(approveCalendar.cancel())
        }
    }, [isApproveSuccess])

    useEffect(() => {
        if (isApprovedCalendarModifyError) {
            setModalsVisibleState({
                ...modalsVisibleState,
                modifyError: true
            })
        }
    }, [isApprovedCalendarModifyError])

    const fillCalendarArrayAndGetFlags = useCallback(() => {
        let activeCalendarItems = cloneDeep(locationCalendars)

        const filteredAdditionalCalendars = activeCalendarItems.filter(
            (el) => el.additionalFields.length && el.locationType === LocationType.Store
        )
        let additionalCalendars: LocationCalendars[] = []

        filteredAdditionalCalendars.forEach((calendar) => {
            const index = locationCalendars.findIndex(
                (item) =>
                    item.locationId === calendar.locationId &&
                    item.locationType === LocationType.Store
            )
            if (index !== -1) {
                additionalCalendars = [
                    ...additionalCalendars,
                    ...locationCalendars[index].additionalFields.map((item) => ({
                        id: item.id,
                        locationId: locationCalendars[index].locationId,
                        locationType: locationCalendars[index].locationType,
                        firstOrderDate: locationCalendars[index].firstOrderDate,
                        lastOrderDate: locationCalendars[index].lastOrderDate,
                        orderDate: item.orderDate,
                        deliveryDate: item.deliveryDate,
                        isNeedToErase: false,
                        additionalFields: []
                    }))
                ]
            }
        })

        activeCalendarItems = [
            ...activeCalendarItems.filter((el) => el.locationType === LocationType.Store),
            ...additionalCalendars
        ]
        let isAllDatesSet = true
        let isAllGapsNormal = true

        activeCalendarItems.forEach((item) => {
            if (
                !item.firstOrderDate ||
                !item.lastOrderDate ||
                (item.deliveryDate && !item.orderDate)
            ) {
                isAllDatesSet = false
            }
            if (isDatesGapIsNormal(item)) {
                isAllGapsNormal = false
            }
        })
        return {
            activeCalendarItems,
            isAllDatesSet,
            isAllGapsNormal
        }
    }, [locationCalendars])

    const saveCalendarRequest = useCallback(
        (activeCalendarItems: LocationCalendars[], isAutoSave: boolean, isApprove) => {
            dispatch(
                saveCalendar.request({
                    calendarItems: activeCalendarItems.map((item) => ({
                        id: item.id,
                        deliveryDate: item.deliveryDate,
                        orderDate: item.orderDate,
                        firstOrderDate: item.firstOrderDate,
                        lastOrderDate: item.lastOrderDate,
                        locationId: item.locationId.toString()
                    })),
                    isAutoSave,
                    isApprove,
                    isEdit: !!calendarData?.dates.length || wasSaved,
                    prevDates: calendarData?.dates || []
                })
            )
        },
        [wasSaved, calendarData?.dates]
    )

    const handleSuccessModalOk = () => {
        setModalsVisibleState({
            ...modalsVisibleState,
            successSave: false
        })
        if (calendarActionState === Approve) {
            history.push(MY_CALENDARS)
        }
    }

    const onApproveCalendar = (isAgreeWithEmptiness: boolean, isAgreeWithGap: boolean) => {
        regionData.length === 0
            ? null
            : sendDatesIfChecksPassed(isAgreeWithEmptiness, isAgreeWithGap, true)
    }

    const onSaveCalendar = useCallback(() => {
        setCalendarActionState(Save)
        const { activeCalendarItems } = fillCalendarArrayAndGetFlags()

        setModalsVisibleState({
            ...modalsVisibleState,
            emptyFields: false,
            bigGap: false
        })
        const filteredCalendar = filterItemsWithDates(activeCalendarItems)
        saveCalendarRequest(filteredCalendar, false, false)
    }, [locationCalendars])

    const sendDatesIfChecksPassed = (
        isAgreeWithEmptiness: boolean,
        isAgreeWithGap: boolean,
        isApproveMethod?: boolean
    ) => {
        const action = isApproveMethod ? Approve : Save
        setCalendarActionState(action)

        const { activeCalendarItems, isAllDatesSet, isAllGapsNormal } =
            fillCalendarArrayAndGetFlags()

        const { isError, errorType } = validateCalendar(activeCalendarItems)

        const text = getEmptyFieldsModalText(errorType, action === Save)

        setEmptyFieldsProps({ text, isError })

        const filteredCalendar = filterItemsWithDates(activeCalendarItems)

        switch (true) {
            case (isAllDatesSet && isAllGapsNormal) ||
                (!isAllDatesSet && isAgreeWithEmptiness && isAllGapsNormal) ||
                (!isAllGapsNormal && isAgreeWithGap):
                setModalsVisibleState({
                    ...modalsVisibleState,
                    emptyFields: false,
                    bigGap: false
                })
                saveCalendarRequest(filteredCalendar, false, isApproveMethod)
                break

            case !isAllGapsNormal || (!isAllDatesSet && isAgreeWithEmptiness && !isAllGapsNormal):
                setModalsVisibleState({
                    ...modalsVisibleState,
                    bigGap: true
                })
                break

            case isError || !isAllDatesSet:
                setModalsVisibleState({
                    ...modalsVisibleState,
                    emptyFields: true
                })
        }
    }

    const onClearCalendar = () => {
        const copyArray = [...locationCalendars]
        copyArray.map((item) => {
            item.deliveryDate = null
            item.orderDate = null
            item.lastOrderDate = null
            item.firstOrderDate = null
            if ('additionalFields' in item) {
                delete (item as { additionalFields?: unknown }).additionalFields
            }
            item.additionalFields = []
        })
        setLocationCalendars(copyArray)
        setIsNeedToErase(true)
    }

    const onDeleteCalendar = () =>
        setModalsVisibleState({
            ...modalsVisibleState,
            deleteConfirm: true
        })

    const handleDeleteCalendar = async () => {
        await appDispatch(deleteCalendar.request(null))
        setModalsVisibleState({
            ...modalsVisibleState,
            deleteConfirm: false
        })
        history.push(MY_CALENDARS)
    }

    const isDatesGapIsNormal = (item: LocationCalendars) =>
        item.firstOrderDate &&
        item.lastOrderDate &&
        Math.floor(
            (item.firstOrderDate.getTime() - item.lastOrderDate.getTime()) / (1000 * 60 * 60 * 24)
        ) > 35

    const saveTitle = isSuperAdmin
        ? 'сохранен как утвержденный. Внести изменения в утвержденные календари можно в меню «Мои календари»'
        : 'сохранен как черновик. Внести изменения в черновики можно в меню «Мои календари»'
    const approveTitle = isSuperAdmin
        ? 'недоступности переутвержден. Вы можете посмотреть все календари в меню «Мои календари»'
        : 'недоступности утвержден. Вы можете посмотреть все календари в меню «Мои календари»'
    const successSaveModalText = `Ваш календарь ${
        calendarActionState === Save ? saveTitle : approveTitle
    }`

    return (
        <Layout
            userInfo={useSelector(userInfoSelector)}
            showToolbar
            toolbarTitle={calendarData?.name || ''}
            actionSection={
                <CalendarActions
                    sendCalendar={() => onApproveCalendar(false, false)}
                    saveCalendar={() => onSaveCalendar()}
                    clearCalendar={onClearCalendar}
                    deleteCalendar={onDeleteCalendar}
                />
            }
        >
            <EmptyFieldsModal
                open={modalsVisibleState.emptyFields}
                onClose={() => setModalsVisibleState({ ...modalsVisibleState, emptyFields: false })}
                onSave={() =>
                    calendarActionState === Save ? onSaveCalendar() : onApproveCalendar(true, false)
                }
                {...emptyFieldsProps}
            />

            <ModifyErrorModal
                open={modalsVisibleState.modifyError}
                onClose={() => setModalsVisibleState({ ...modalsVisibleState, modifyError: false })}
            />

            <ApproveConfirmModal
                open={modalsVisibleState.approveConfirm}
                onClose={() =>
                    setModalsVisibleState({ ...modalsVisibleState, approveConfirm: false })
                }
                onApprove={() => onApproveCalendar(true, true)}
            />

            <BigGapModal
                open={modalsVisibleState.bigGap}
                onClose={() => setModalsVisibleState({ ...modalsVisibleState, bigGap: false })}
                onSave={() =>
                    calendarActionState === Save ? onSaveCalendar() : onApproveCalendar(false, true)
                }
            />

            <SuccessSaveModal
                open={modalsVisibleState.successSave}
                text={successSaveModalText}
                onClose={() => setModalsVisibleState({ ...modalsVisibleState, successSave: false })}
                onSave={handleSuccessModalOk}
            />

            <DeleteConfirmModal
                open={modalsVisibleState.deleteConfirm}
                onClose={() =>
                    setModalsVisibleState({ ...modalsVisibleState, deleteConfirm: false })
                }
                onDelete={handleDeleteCalendar}
            />

            <Calendar
                type={CalendarType.CREATE}
                openBtnState={openBtnState}
                regionData={regionData}
                calendarData={calendarData}
                locationCalendars={locationCalendars}
                isNeedToErase={isNeedToErase}
                calendarId={calendarId}
                setOpenBtnState={setOpenBtnState}
                setRegionData={setRegionData}
                setCalendarData={setCalendarData}
                setLocationCalendars={setLocationCalendars}
                fillCalendarArrayAndGetFlags={fillCalendarArrayAndGetFlags}
                saveCalendarRequest={saveCalendarRequest}
            />
        </Layout>
    )
}
